import { Button } from '@atoms';
import { Coupon, ProductNode } from '@datamodel';
import { SmartWrapView } from '@lib';
import { Mixins, Spacing, Typography } from '@styles';
import { useTheme } from '@themeProvider';
import Animation from '@utils/animations';
import { parsePrice } from '@utils/helpers';
import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';

const PackItem = ({ cartCount = 1, packItem, style, photoStyle, ...props }) => {
    const { useColor } = useTheme();
    const [body, title, line] = useColor(['body', 'title', 'line']);

    // const contrastedPrimary = Colors.withMinimumWhiteContrast(primary, 18);

    return <View style={[Mixins.HORIZONTAL, { height: 28, paddingHorizontal: Spacing.XS, alignItems: 'center', borderWidth: 1, borderColor: line, gap: 0, borderRadius: 16, }, style]} {...props}>
        <Text style={[Typography.SMALL_TITLE, { color: title, textAlign: 'center', zIndex: 1 }]}>{cartCount * packItem.quantity}</Text>
        <Text style={[Typography.SMALL_COPY, { color: body, marginLeft: Spacing.XS }]}>{packItem.product.name}</Text>
    </View>
}

export const ProductPackCard = ({ pack, style, cartCount, onPress, ...props }) => {
    const animation = useRef(new Animated.Value(0)).current;
    const { useColor, useElevation } = useTheme();
    const elevation = useElevation(4);
    const rounding = Mixins.rounding(88);
    const [title, body, primary, primaryText, line, label, backgroundColor] = useColor([
        'title',
        'body',
        'primary',
        ['primary', { isText: true }],
        'line',
        'label',
        'background',
    ])

    const [numberColor] = useColor([
        ['title', { on: primary }]
    ]);

    const packItems = Object.keys(pack.items).map(id => pack.items[id]);

    const [price, originalPrice] = useMemo(() => {
        return Object.keys(pack.items).reduce((acc, itemId) => {
            const item = pack.items[itemId];
            const fromPrice = ProductNode.calculateTreeFromPrices(item.product);

            // TODO: Check this
            let unitPrice = item.product.maxChildren === 0 ? item.product.price.amount : fromPrice[item.product.nodeId] + item.product.price.amount;
            let itemPrice = unitPrice * item.quantity;
            let ogItemPrice = itemPrice;
            if (item.coupon) {
                itemPrice = Coupon.discountedPrice(item.coupon, ogItemPrice);
            }
            return [acc[0] + itemPrice, acc[1] + ogItemPrice];
        }, [0, 0]);
    });

    useEffect(() => {
        if (!cartCount) {
            Animated.timing(animation, Animation.short(0)).start();
        } else if (cartCount === 1) {
            Animated.timing(animation, Animation.short(1)).start();
        }
    }, [cartCount]);

    const onMinusPress = () => {
        // Find pack in cart
        const cartPackIndex = Object.keys(props.stackedCart.packs).find(packIndex => {
            const cartPack = props.stackedCart.packs[packIndex];
            return cartPack.packId === pack.packId;
        });
        if (!cartPackIndex) {
            return;
        }
        const cartPack = props.stackedCart.packs[cartPackIndex];
        props.setPackQuantity(cartPackIndex, cartPack)(0);
    }

    return <Pressable style={[elevation, rounding, { paddingVertical: Spacing.S }, style]} {...props}>
        <View style={[Mixins.HORIZONTAL, { flex: 1, alignItems: 'center', paddingHorizontal: Spacing.M },]}>
            <Text style={[styles.title, { color: title, flex: 1 }]}>{pack.name}</Text>
            <Text style={[styles.title, { color: title }]}>{parsePrice(price, '€')}  </Text>

            <View style={[Mixins.HORIZONTAL, { gap: Spacing.XS, justifyContent: 'center', alignItems: 'center' }]}>
                <Animated.View style={[Mixins.HORIZONTAL, {
                    gap: Spacing.XS, justifyContent: 'center', alignItems: 'center',
                    opacity: 1,
                    width: Button.HEIGHT + .5 * styles.title.fontSize + Spacing.XS,
                }]}>
                    <Button onPress={onMinusPress} status={cartCount > 0 ? 'default' : 'disabled'} color="primary" icon="minus-alt" size="x-condensed" style={{ width: 28, padding: 0, justifyContent: 'center' }} />
                    <Text style={[styles.title, { color: title, flex: 1, textAlign: 'center' }]}>{cartCount || 0}</Text>
                </Animated.View>
                <Button onPress={onPress} color="primary" icon="add-alt" size="x-condensed" style={{ width: 28, padding: 0, justifyContent: 'center' }} />
            </View>
        </View>

        {/* <Text style={[Typography.SUBTITLE, { color: body, marginHorizontal: Spacing.S, marginTop: Spacing.XS }]}>{pack.description}</Text> */}
        <Animated.View style={{
            borderTopWidth: 1, borderColor: line, paddingRight: Spacing.M, paddingLeft: Spacing.XS,
            marginTop: animation.interpolate(Animation.keyframes(0, Spacing.XS)),
            opacity: animation.interpolate(Animation.keyframes(0, 1)),
            maxHeight: animation.interpolate(Animation.keyframes(0, 100)),
        }}>
            <SmartWrapView flexDirection='row' crossDirectionSize={40 + Spacing.XS} style={{ paddingTop: Spacing.XS, gap: Spacing.XS }}>
                {packItems.map(packItem => <PackItem style={{ flexGrow: 1 }} cartCount={cartCount} key={packItem.itemId} packItem={packItem} photoStyle={Mixins.matchRounding(rounding, -Spacing.S)} />)}
            </SmartWrapView>
        </Animated.View>
    </Pressable>
}

const styles = StyleSheet.create({
    title: {
        fontSize: 14,
        lineHeight: 20,
        fontFamily: 'Poppins-SemiBold'
    },
    body: {
        fontSize: 14,
        lineHeight: 20,
        letterSpacing: '4%',
        fontFamily: 'Poppins-Regular'

    }
})
